/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { AsyncPipe } from "@angular/common";
import { Component, inject, OnDestroy } from "@angular/core";
import { first, map, Observable, Subject, switchMap } from "rxjs";

import { ApplicationState } from "../../../service/application-state.service";
import { PageScaffoldComponent, ResourceAvailability } from "../../scaffold/page/page-scaffold.component";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute, RouterLink, RouterOutlet } from "@angular/router";
import { MatSidenavModule } from "@angular/material/sidenav";
import { checkScrollLocationFromIDs } from "../../../util";
import {
    SecurityCenterReauthenticateDialogComponent
} from "./reauthenticate-dialog/security-center-reauthenticate-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { ButtonComponent } from "typedb-cloud-framework";

const REAUTH_TIMEOUT = 3600_000;

@Component({
    selector: "tp-security-center-scaffold",
    templateUrl: "./security-center-scaffold.component.html",
    styleUrls: ["./security-center-scaffold.component.scss", "./security-center.scss"],
    imports: [
        PageScaffoldComponent, AsyncPipe, ButtonComponent,
        MatTooltipModule, RouterLink, MatSidenavModule, RouterOutlet
    ]
})
export class SecurityCenterScaffoldComponent implements OnDestroy {
    private app = inject(ApplicationState);
    private route = inject(ActivatedRoute);
    private dialog = inject(MatDialog);

    reauthRequired: boolean;

    readonly team = this.app.requireCurrentTeam();
    readonly unsub$ = new Subject<void>();
    readonly availability$: Observable<ResourceAvailability> = this.app.hasTeamAdminAccess$.pipe(map(x => x ? "ready" : "loading"));

    get activeSection() {
        return this.route.snapshot.firstChild?.data["securitySection"];
    }

    constructor() {
        const lastReauth = this.app.getLastSecurityCenterAuth();
        if (!lastReauth || ((new Date()).getTime() - lastReauth.getTime()) > REAUTH_TIMEOUT) {
            this.reauthRequired = true;
            this.openReauthDialog()
        } else {
            this.reauthRequired = false;
        }

        this.route.fragment.pipe(
            switchMap(fragment => this.availability$.pipe(
                first(x => x === "ready"),
                map(_ => fragment),
            ))
        ).subscribe(fragment => {
            setTimeout(() => {
                const el = document.getElementById(fragment!);
                if (el) el.scrollIntoView(true);
            });
        });
    }

    ngOnDestroy() {
        this.unsub$.next();
    }

    overviewScrollElementIDs = ["score", "recommendations"];
    configScrollElementIDs = ["mfa-policy", "team-admins", "security-contacts"];
    currentActive = 'top';

    checkScrollLocation($event: any) {
        const scrollTop = $event.target.scrollTop + 78; // Account for height of header
        if (this.activeSection === "overview") {
            this.currentActive = checkScrollLocationFromIDs(this.overviewScrollElementIDs, scrollTop) ?? 'top';
        } else if (this.activeSection === "config") {
            this.currentActive = checkScrollLocationFromIDs(this.configScrollElementIDs, scrollTop) ?? 'top';
        }
    }

    openReauthDialog() {
        this.dialog.open(SecurityCenterReauthenticateDialogComponent).beforeClosed().subscribe(result => {
            if (result === "ok") this.reauthRequired = false;
        });
    }
}
